/**
 * A phone's full-screen sheet for typing: a compose that takes the whole screen and, with the keyboard up, exactly the
 * room above it. It sizes from the app's one keyboard-aware rule, `h-safe-dvh` in `index.css`
 * (`usePwaKeyboardSafeArea` sets `html.keyboard-open`, `--app-vh` and `--app-vv-top`; iOS lays the keyboard over the
 * page instead of resizing it), the rule the Kory Code shell and `DIALOG_FIT` already use. No new measuring.
 *
 * It is a modal dialog of its own, so it sits over a record tray and closes alone (`components/ui/nested-layer.ts`):
 * Escape or the child's own Cancel closes it, never the tray beneath. The child brings its own bar and title (the
 * email composer's Cancel, "Email <name>", Send).
 */

import type { ReactNode } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { OverlayScope } from "@/components/ui/overlay-scope";
import { cn } from "@/lib/utils";

export function KeyboardSheet({
  open,
  onOpenChange,
  title,
  className,
  testId,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The sheet's accessible name ("Email Richard staab"); the child shows its own title. */
  title: string;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          data-testid={testId}
          aria-describedby={undefined}
          // Never auto-focus here: the child focuses its body itself, which is what raises the keyboard.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            // The whole screen under the status bar; with the keyboard up, the visible viewport above it.
            "bg-background fixed inset-x-0 top-0 z-50 flex h-safe-dvh flex-col pt-[var(--app-safe-top)] outline-none",
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <OverlayScope>{children}</OverlayScope>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
